/**
 * Workflow Project — detail view.
 *
 * Manage one project's members: add/remove workflows, set the declared run
 * order (move up/down), nominate the entry point, fire a single member's
 * schedule (Run now), or run the whole project in order (Run project).
 * Chain the workflows so each starts after the one before it, and see the
 * chain's runs, paged (`ProjectChain`, dp plan workflow-chaining-after-workflow).
 *
 * Member schedule labels + the per-member "Run now" gate come from the org's
 * definitions (fetched alongside the project), since a project member is just a
 * WorkflowKey — the DSL lives on its definition.
 *
 * Owner/admin only.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  ArrowLeft,
  FolderTree,
  Loader2,
  Play,
  Plus,
  Repeat,
  ShieldOff,
  Star,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  hasScheduleTrigger,
  isContinuousSchedule,
  scheduleLabel,
  scheduleSortKey,
} from "@/components/portal/workflow-flow-v2/workflowScheduleLabel";
import {
  ProjectHealthChip,
  LastRunsUnavailable,
  WorkflowLastRunCell,
  projectHealth,
} from "@/components/portal/workflow-flow-v2/workflowLastRun";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/ui/page-header";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { ProjectConnections } from "@/components/portal/workflows/ProjectConnections";
import {
  ProjectChain,
  type ChainBuildFailure,
} from "@/components/portal/workflows/ProjectChain";
import {
  mapConnector,
  typeMetaFor,
  type FetchedConnector,
} from "@/lib/connectorSummary";
import { useAnyRefreshPending } from "@/lib/connectorRefreshPending";
import { PortalApiError, portalFetch } from "@/lib/portalApi";
import { chainOrder } from "@/lib/workflowChain";
import { splitConnectorMembers } from "@/lib/projectConnectorMembers";
import type { B2CConnectorType, PortalConnectorSummary } from "@/types/portal";
import {
  type ChainBuildMember,
  type ChainRunsResponse,
  type DefinitionResponse,
  type WorkflowLastRun,
  type ProjectMember,
  type WorkflowProject,
  workflowMemberKeys,
  workflowsApi,
} from "@/services/api/workflows";

export const Route = createFileRoute(
  "/portal/workflows/projects_/$projectId",
)({
  component: ProjectDetailPage,
});

/** Latest definition per WorkflowKey — gives each member its DSL (for the
 * schedule label + Run-now gate) without a per-member fetch. */
function latestByKey(
  defs: DefinitionResponse[],
): Map<string, DefinitionResponse> {
  const map = new Map<string, DefinitionResponse>();
  for (const d of defs) {
    const prev = map.get(d.workflow_key);
    if (!prev || d.version > prev.version) map.set(d.workflow_key, d);
  }
  return map;
}

/** One project id however it is written: dp returns lowercase hyphenated
 *  UUIDs but accepts any case or no hyphens in the URL. */
const sameProjectId = (a: string, b: string) =>
  a.replace(/-/g, "").toLowerCase() === b.replace(/-/g, "").toLowerCase();

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const { token } = useAuth();
  const { isAdmin, isPlatformAdmin } = usePermissions();
  const { selectedOrganisation } = usePortalData();
  const canManage = isAdmin || isPlatformAdmin;
  const navigate = useNavigate();

  const [project, setProject] = useState<WorkflowProject | null>(null);
  const [defs, setDefs] = useState<DefinitionResponse[]>([]);
  const [lastRuns, setLastRuns] = useState<WorkflowLastRun[]>([]);
  const [lastRunsFailed, setLastRunsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addKeys, setAddKeys] = useState<Set<string>>(new Set());
  const [isBusy, setIsBusy] = useState(false);

  const [runningProject, setRunningProject] = useState(false);
  const [runningMembers, setRunningMembers] = useState<Set<string>>(new Set());

  // Connector members: the org's connector rows + type catalogue, so a member
  // id can be rendered as the same card every other connector surface shows.
  const [orgConnectors, setOrgConnectors] = useState<FetchedConnector[]>([]);
  const [connectorTypes, setConnectorTypes] = useState<B2CConnectorType[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  // Fetched SUCCESSFULLY at least once. Distinct from "not loading": a failed
  // fetch must not be read as "this org has no connectors".
  const [connectorsLoaded, setConnectorsLoaded] = useState(false);
  const [busyConnectorId, setBusyConnectorId] = useState<number | null>(null);
  const [isAddConnOpen, setIsAddConnOpen] = useState(false);
  const [addConnIds, setAddConnIds] = useState<Set<number>>(new Set());

  // The project's chain links (the runs are paged by the Chain runs card).
  // null = not loaded or failed;
  // `chainFailed` tells the two apart so a failed read is never shown as
  // "not chained".
  const [chain, setChain] = useState<ChainRunsResponse | null>(null);
  const [chainFailed, setChainFailed] = useState(false);
  const [buildingChain, setBuildingChain] = useState(false);
  const [lastBuild, setLastBuild] = useState<ChainBuildMember[] | null>(null);
  const [buildFailure, setBuildFailure] = useState<ChainBuildFailure | null>(
    null,
  );

  const defByKey = useMemo(() => latestByKey(defs), [defs]);

  // One page of chain runs for the Chain runs card. Keyed on `chain` so a page
  // reload (after a build, a run, an add or a remove) hands the card a new
  // function, and it re-reads the page it is on (the card stays mounted).
  const loadChainRuns = useCallback(
    (limit: number, offset: number) =>
      workflowsApi.getProjectChainRuns(projectId, limit, token ?? undefined, offset),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, token, chain],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [proj, definitions, runs, chainRead] = await Promise.all([
        workflowsApi.getProject(projectId, token ?? undefined),
        workflowsApi.listDefinitions(token ?? undefined),
        workflowsApi
          .listLastRuns(token ?? undefined)
          .then((r) => r.rows)
          .catch(() => null),
        // Best-effort, like the last runs: a chain-read hiccup must not blank
        // the project.
        // Only the links are read here; the Chain runs card pages the runs.
        workflowsApi
          .getProjectChainRuns(projectId, 1, token ?? undefined)
          .catch(() => null),
      ]);
      setProject(proj);
      setDefs(definitions);
      setLastRuns(runs ?? []);
      setLastRunsFailed(runs === null);
      const chainOk =
        chainRead !== null &&
        Array.isArray(chainRead.links) &&
        Array.isArray(chainRead.runs);
      setChain(chainOk ? chainRead : null);
      setChainFailed(!chainOk);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load project");
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token]);

  // Re-pull just the last-run signal after firing, so the member cells + the
  // pipeline-health chip reflect the runs just triggered (clearing overdue)
  // without a full reload. Best-effort — failure keeps the prior values.
  const refreshLastRuns = useCallback(async () => {
    try {
      const res = await workflowsApi.listLastRuns(token ?? undefined);
      setLastRuns(res.rows);
      setLastRunsFailed(false);
    } catch {
      /* keep the existing signal */
    }
  }, [token]);

  useEffect(() => {
    if (canManage) void load();
  }, [load, canManage]);

  // Best-effort and SEPARATE from `load`: a b2c hiccup must not blank the
  // project itself. The connections section degrades to "no connections"
  // rather than taking the workflow table down with it.
  //
  // `silent` is for refresh polling: no loading state (that swaps the section
  // for "Loading…" and unmounts every connector card mid-refresh), and a
  // failure keeps the rows already on screen.
  const loadConnectors = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!selectedOrganisation) return;
    if (!silent) setConnectorsLoading(true);
    try {
      const orgId = selectedOrganisation.organisationId;
      const [rows, types] = await Promise.all([
        portalFetch<FetchedConnector[]>(
          `/api/v1/gateway/business2cloud/api/v1/connectors/org/${orgId}`,
          { token: token ?? undefined },
        ),
        portalFetch<B2CConnectorType[]>(
          `/api/v1/gateway/business2cloud/api/v1/connectors/types/org/${orgId}`,
          { token: token ?? undefined },
        ).catch(() => [] as B2CConnectorType[]),
      ]);
      setOrgConnectors(rows ?? []);
      setConnectorTypes(types ?? []);
      setConnectorsLoaded(true);
    } catch {
      if (silent) return;
      setOrgConnectors([]);
      setConnectorsLoaded(false);
    } finally {
      if (!silent) setConnectorsLoading(false);
    }
  }, [selectedOrganisation, token]);

  useEffect(() => {
    if (canManage) void loadConnectors();
  }, [loadConnectors, canManage]);

  // Definition keys not yet in THIS project — candidates for "Add workflows".
  // (A key already in ANOTHER project is still offered; the bulk add reports it
  // as skipped, since we don't fetch other projects here.)
  const candidateKeys = useMemo(() => {
    const inProject = new Set(workflowMemberKeys(project?.members ?? []));
    return Array.from(defByKey.keys())
      .filter((k) => !inProject.has(k))
      .sort();
  }, [defByKey, project?.members]);

  const toggleAddKey = useCallback((key: string) => {
    setAddKeys((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleAddMembers = useCallback(async () => {
    if (addKeys.size === 0) return;
    setIsBusy(true);
    try {
      const res = await workflowsApi.addProjectMembersBulk(
        projectId,
        Array.from(addKeys),
        token ?? undefined,
      );
      setProject(res.project);
      setIsAddOpen(false);
      setAddKeys(new Set());
      const addedCount = addKeys.size - res.skipped.length;
      if (res.skipped.length > 0) {
        toast.warning(
          `Added ${addedCount}; skipped ${res.skipped.length} already in another project: ${res.skipped.map((s) => s.workflow_key).join(", ")}`,
        );
      } else {
        toast.success(
          `Added ${addedCount} workflow${addedCount === 1 ? "" : "s"}`,
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add workflows");
    } finally {
      setIsBusy(false);
    }
  }, [addKeys, projectId, token]);

  const handleRemoveMember = useCallback(
    async (workflowKey: string) => {
      setIsBusy(true);
      try {
        await workflowsApi.removeProjectMember(
          projectId,
          workflowKey,
          token ?? undefined,
        );
        // The backend nulls EntryWorkflowKey itself if we removed the entry
        // member, so the pointer can't dangle. Just re-read the authoritative
        // state.
        setProject(await workflowsApi.getProject(projectId, token ?? undefined));
        toast.success(`Removed ${workflowKey}`);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to remove member",
        );
      } finally {
        setIsBusy(false);
      }
    },
    [projectId, token],
  );


  const handleSetEntry = useCallback(
    async (workflowKey: string) => {
      setIsBusy(true);
      try {
        await workflowsApi.updateProject(
          projectId,
          { entry_workflow_key: workflowKey },
          token ?? undefined,
        );
        // updateProject omits members; re-read to keep the table populated.
        setProject(await workflowsApi.getProject(projectId, token ?? undefined));
        toast.success(`Entry point set to ${workflowKey}`);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to set entry point",
        );
      } finally {
        setIsBusy(false);
      }
    },
    [projectId, token],
  );

  const handleRunMember = useCallback(
    async (workflowKey: string) => {
      setRunningMembers((s) => new Set(s).add(workflowKey));
      try {
        const res = await workflowsApi.runScheduleNow(
          workflowKey,
          token ?? undefined,
        );
        if (res.executions_fired === 0) {
          toast.info(`No work for ${workflowKey} right now.`);
        } else {
          const single =
            res.execution_ids.length === 1 ? res.execution_ids[0] : undefined;
          toast.success(
            `Fired ${res.executions_fired} execution${res.executions_fired === 1 ? "" : "s"} for ${workflowKey}`,
            {
              action: {
                label: "View",
                onClick: () =>
                  void navigate({
                    to: "/portal/workflows/executions",
                    search: single
                      ? { open: single }
                      : { workflow: workflowKey },
                  }),
              },
            },
          );
          void refreshLastRuns();
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to run workflow");
      } finally {
        setRunningMembers((s) => {
          const next = new Set(s);
          next.delete(workflowKey);
          return next;
        });
      }
    },
    [token, navigate, refreshLastRuns],
  );

  const handleAddConnectors = useCallback(async () => {
    if (addConnIds.size === 0) return;
    setIsBusy(true);
    try {
      // One call per connector: the API is per-connector so a rejected one
      // (not yours / already in another project) must not take the rest with
      // it. Failures are reported individually, same posture as the bulk
      // workflow add.
      const failures: string[] = [];
      for (const id of addConnIds) {
        try {
          await workflowsApi.addProjectConnectorMember(
            projectId,
            id,
            token ?? undefined,
          );
        } catch (e) {
          failures.push(
            `${id}: ${e instanceof Error ? e.message : "failed"}`,
          );
        }
      }
      const added = addConnIds.size - failures.length;
      if (added > 0) {
        toast.success(
          `Added ${added} connection${added === 1 ? "" : "s"} to the project.`,
        );
      }
      if (failures.length > 0) {
        toast.error(`Could not add ${failures.length}: ${failures.join("; ")}`);
      }
      // Only close and clear when something actually landed. Closing on a
      // total failure discards the user's picks and leaves them to rebuild
      // the list from a toast.
      if (added > 0) {
        setIsAddConnOpen(false);
        setAddConnIds(new Set());
        await load();
      }
    } finally {
      setIsBusy(false);
    }
  }, [addConnIds, projectId, token, load]);

  const handleRemoveConnector = useCallback(
    async (connectorId: number) => {
      // Ignore a second remove while one is in flight: `busyConnectorId` holds
      // ONE id, so a concurrent click would blank the first spinner and then
      // have its own cleared by the first request's `finally`.
      if (busyConnectorId !== null) return;
      setBusyConnectorId(connectorId);
      try {
        await workflowsApi.removeProjectConnectorMember(
          projectId,
          connectorId,
          token ?? undefined,
        );
        // Re-read rather than splice locally: the response is authoritative
        // about the remaining member set and its order.
        await load();
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to remove connection",
        );
      } finally {
        setBusyConnectorId(null);
      }
    },
    [projectId, token, load, busyConnectorId],
  );

  const handleRunProject = useCallback(async () => {
    if (!project) return;
    setRunningProject(true);
    try {
      const res = await workflowsApi.runProject(
        project.id,
        token ?? undefined,
      );
      if (res.members_fired === 0) {
        toast.info(
          `${project.name} has no schedule-driven members to run.`,
        );
      } else if (res.executions_fired === 0) {
        toast.info(
          `Ran ${project.name} — ${res.members_fired} member${res.members_fired === 1 ? "" : "s"} scheduled, no work to do right now.`,
        );
      } else {
        const projectId = project.id;
        toast.success(
          `Ran ${project.name} — fired ${res.executions_fired} execution${res.executions_fired === 1 ? "" : "s"} across ${res.members_fired} member${res.members_fired === 1 ? "" : "s"}.`,
          {
            action: {
              label: "View",
              onClick: () =>
                void navigate({
                  to: "/portal/workflows/executions",
                  search: { project: projectId },
                }),
            },
          },
        );
      }
      if (res.members_fired > 0) void refreshLastRuns();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to run project");
    } finally {
      setRunningProject(false);
    }
  }, [project, token, navigate, refreshLastRuns]);

  const handleBuildChain = useCallback(
    async (orderedKeys: string[]) => {
      setBuildingChain(true);
      setBuildFailure(null);
      try {
        const res = await workflowsApi.buildProjectChain(
          projectId,
          orderedKeys,
          token ?? undefined,
        );
        setLastBuild(res.members);
        const published = res.members.filter((m) => m.action !== "unchanged");
        if (published.length === 0) {
          toast.info("The chain was already in this order — nothing published.");
        } else {
          toast.success(
            `Chain built — published ${published.length} workflow${published.length === 1 ? "" : "s"}.`,
          );
        }
        // Versions, order and entry all moved: re-read everything.
        await load();
      } catch (e) {
        // A failure part-way through answers with the members already
        // published (dp projects.build_chain): show them, they are live.
        const detail =
          e instanceof PortalApiError &&
          e.details &&
          typeof e.details === "object"
            ? (e.details as { detail?: unknown }).detail
            : undefined;
        const structured =
          detail && typeof detail === "object"
            ? (detail as {
                failed_member?: unknown;
                published?: unknown;
              })
            : null;
        setLastBuild(null);
        setBuildFailure({
          message: e instanceof Error ? e.message : "Failed to build the chain",
          failedMember:
            typeof structured?.failed_member === "string"
              ? structured.failed_member
              : null,
          published: Array.isArray(structured?.published)
            ? (structured.published as ChainBuildMember[])
            : [],
        });
        toast.error(e instanceof Error ? e.message : "Failed to build the chain");
        // Something went live before the failure: re-read versions and links.
        if (Array.isArray(structured?.published) && structured.published.length > 0) {
          await load();
        }
      } finally {
        setBuildingChain(false);
      }
    },
    [projectId, token, load],
  );

  // Order by SCHEDULE, not add-order — the cron is the pipeline sequence
  // (po-readiness 01:00 → asana 01:20 → …; continuous pollers last). This
  // matches the backend's run_project fire order, so the operator never
  // hand-orders. Stable sort keeps equal-schedule members in their existing
  // order. (Declared before the early return so the hook order is stable.)
  const members = useMemo(() => {
    // WORKFLOW members only. The step table numbers a run sequence, and a
    // connector is not a step in it — it is what the steps talk to. A
    // connector member here would also carry workflow_key === null straight
    // into defByKey/lastRunByKey and render a blank row.
    const list = (project?.members ?? []).filter(
      (m): m is ProjectMember & { workflow_key: string } =>
        m.workflow_key !== null,
    );
    // A chained project runs in CHAIN order, not schedule order: only its
    // first member has a trigger of its own.
    if (chain && chain.links.length > 0) {
      const byKey = new Map(list.map((m) => [m.workflow_key, m]));
      return chainOrder(
        list.map((m) => m.workflow_key),
        chain.links,
      ).map((k) => byKey.get(k) as (typeof list)[number]);
    }
    return list
      .map((m, i) => ({
        m,
        i,
        key: scheduleSortKey(defByKey.get(m.workflow_key)?.workflow),
      }))
      .sort((a, b) => a.key - b.key || a.i - b.i)
      .map((x) => x.m);
  }, [project?.members, defByKey, chain]);

  const memberKeys = useMemo(
    () => members.map((m) => m.workflow_key),
    [members],
  );

  // Connector members, resolved against the org's connector list into the
  // SAME summary shape every other connector surface renders.
  const connectorMemberIds = useMemo(
    () =>
      (project?.members ?? [])
        .filter((m) => m.client_connector_id !== null)
        .map((m) => m.client_connector_id as number),
    [project?.members],
  );

  const { projectConnectors, unresolvedConnectorIds } = useMemo(() => {
    if (!selectedOrganisation) {
      return {
        projectConnectors: [] as PortalConnectorSummary[],
        unresolvedConnectorIds: [] as number[],
      };
    }
    const { resolved, unresolvedIds } = splitConnectorMembers(
      connectorMemberIds,
      orgConnectors,
      connectorsLoaded,
    );
    return {
      projectConnectors: resolved.map((raw) =>
        mapConnector(raw, selectedOrganisation, typeMetaFor(raw, connectorTypes)),
      ),
      unresolvedConnectorIds: unresolvedIds,
    };
  }, [
    connectorMemberIds,
    orgConnectors,
    connectorTypes,
    selectedOrganisation,
    connectorsLoaded,
  ]);

  // A refresh clicked on a project's connector card is only confirmed by
  // re-pulling the connector rows, so reload after the click and poll while a
  // refresh waits for its b2c worker (5s) or a run is in progress (30s).
  const reloadConnectorsSilently = useCallback(() => {
    void loadConnectors({ silent: true });
  }, [loadConnectors]);
  const projectConnectorIds = useMemo(
    () => projectConnectors.map((connector) => connector.id),
    [projectConnectors],
  );
  const hasPendingConnectorRefresh = useAnyRefreshPending(projectConnectorIds);
  const hasRefreshingConnectors =
    hasPendingConnectorRefresh ||
    projectConnectors.some((connector) => connector.refreshInProgress);

  useEffect(() => {
    if (!canManage || !hasRefreshingConnectors) return;
    const interval = setInterval(
      reloadConnectorsSilently,
      hasPendingConnectorRefresh ? 5_000 : 30_000,
    );
    return () => clearInterval(interval);
  }, [
    canManage,
    hasRefreshingConnectors,
    hasPendingConnectorRefresh,
    reloadConnectorsSilently,
  ]);

  // Connectors not yet in THIS project — candidates for "Add connection".
  const candidateConnectors = useMemo(() => {
    const taken = new Set(connectorMemberIds);
    return orgConnectors
      .filter((c) => !taken.has(c.Id))
      .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
  }, [orgConnectors, connectorMemberIds]);

  // Last-run signal per member + the project's rolled-up health.
  const lastRunByKey = useMemo(() => {
    const m = new Map<string, WorkflowLastRun>();
    for (const r of lastRuns) m.set(r.workflow_key, r);
    return m;
  }, [lastRuns]);

  const health = useMemo(
    () =>
      projectHealth(
        members.map((m) => m.workflow_key),
        lastRunByKey,
      ),
    [members, lastRunByKey],
  );

  if (!canManage) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Workflow Project"
          description="Manage a project's workflows."
          icon={FolderTree}
          eyebrow="Authoring"
        />
        <Alert>
          <ShieldOff className="h-4 w-4" />
          <AlertDescription>
            Workflows are managed by organisation owners and admins. You don't
            have access to this area.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // One write at a time across the page — a mutation (add/remove/entry) and a
  // fire (run-now/run-project) must not race on the member set. Each button
  // still keys its OWN spinner off its specific in-flight state.
  const locked =
    isBusy ||
    runningProject ||
    runningMembers.size > 0 ||
    // Connector add/remove is a write on the same member set. Leaving it out
    // made the comment above false: "Run project", "Set entry" and the
    // workflow removes all stayed live during a connector DELETE.
    busyConnectorId !== null ||
    // A chain build publishes members and rewrites order + entry.
    buildingChain;

  return (
    <>
      <Helmet>
        <title>{project ? `${project.name} | Projects` : "Project"} | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          className="w-fit"
          onClick={() => void navigate({ to: "/portal/workflows/projects" })}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          All projects
        </Button>

        <PageHeader
          title={project?.name ?? "Project"}
          description={
            project?.description ?? "Group related workflows into one pipeline."
          }
          icon={FolderTree}
          eyebrow="Project"
        >
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsAddOpen(true)}
              disabled={locked}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add workflow
            </Button>
            <Button
              className="border-emerald-200 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => void handleRunProject()}
              disabled={locked || members.length === 0}
            >
              {runningProject ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run project
            </Button>
          </div>
        </PageHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {lastRunsFailed && <LastRunsUnavailable />}

        {!isLoading && members.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Pipeline health:</span>
            <ProjectHealthChip health={health} />
            {health.status === "degraded" && (
              <span className="text-muted-foreground">{health.reason}</span>
            )}
          </div>
        )}

        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading…
              </div>
            ) : members.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                No workflows in this project yet. Add one to build the pipeline.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Last run</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    let step = 0;
                    return members.map((member) => {
                    const def = defByKey.get(member.workflow_key);
                    const wf = def?.workflow;
                    const isEntry =
                      project?.entry_workflow_key === member.workflow_key;
                    const runnable = wf ? hasScheduleTrigger(wf) : false;
                    const continuous = wf ? isContinuousSchedule(wf) : false;
                    // Number only the sequential (timed) steps; continuous
                    // pollers aren't a step in the chain.
                    if (!continuous) step += 1;
                    return (
                      <TableRow key={member.workflow_key}>
                        <TableCell className="text-muted-foreground">
                          {continuous ? (
                            <Repeat className="h-3.5 w-3.5" aria-label="continuous" />
                          ) : (
                            step
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {member.workflow_key}
                          {isEntry && (
                            <Badge className="ml-2 bg-violet-100 text-violet-700 hover:bg-violet-100">
                              <Star className="mr-1 h-3 w-3" />
                              Entry
                            </Badge>
                          )}
                          {continuous && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-muted-foreground"
                            >
                              continuous
                            </Badge>
                          )}
                          {!def && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-amber-600"
                            >
                              no definition
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {wf ? scheduleLabel(wf) : "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <WorkflowLastRunCell
                            run={lastRunByKey.get(member.workflow_key)}
                          />
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          {!isEntry && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={locked}
                              onClick={() =>
                                void handleSetEntry(member.workflow_key)
                              }
                            >
                              <Star className="mr-1 h-3.5 w-3.5" />
                              Set entry
                            </Button>
                          )}
                          {runnable && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                              disabled={locked}
                              onClick={() =>
                                void handleRunMember(member.workflow_key)
                              }
                              aria-label={`Run ${member.workflow_key} now`}
                            >
                              {runningMembers.has(member.workflow_key) ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Play className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1">Run now</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            disabled={locked}
                            onClick={() =>
                              void handleRemoveMember(member.workflow_key)
                            }
                            aria-label={`Remove ${member.workflow_key}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  });
                  })()}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Not gated on isLoading: a reload must not unmount the card, or it
            drops the reader back to page 1. Gated on the loaded project being
            the one in the URL instead: browser back/forward between two
            projects keeps this page mounted, and until the new project lands
            `project` and `chain` still hold the previous one. */}
        {project && sameProjectId(project.id, projectId) && members.length > 1 && (
          <ProjectChain
            key={projectId}
            chainKeys={memberKeys}
            chain={chain}
            chainUnavailable={chainFailed}
            canManage={canManage}
            locked={locked}
            building={buildingChain}
            lastBuild={lastBuild}
            buildFailure={buildFailure}
            onBuild={(keys) => void handleBuildChain(keys)}
            loadRuns={loadChainRuns}
          />
        )}

        <ProjectConnections
          connectors={projectConnectors}
          unresolvedIds={unresolvedConnectorIds}
          isLoading={connectorsLoading || isLoading}
          canManage={canManage}
          locked={locked}
          busyConnectorId={busyConnectorId}
          onAdd={() => setIsAddConnOpen(true)}
          onRemove={(id) => void handleRemoveConnector(id)}
          onConnectorRefreshed={reloadConnectorsSilently}
        />
      </div>

      {/* Add members dialog — multi-select */}
      <Dialog
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) setAddKeys(new Set());
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add workflows</DialogTitle>
            <DialogDescription>
              Select the workflows to add to this project. They're appended in
              the order you'll see them; a workflow can only belong to one
              project.
            </DialogDescription>
          </DialogHeader>
          {candidateKeys.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every workflow is already in this project (or you have none yet —
              create one under Definitions first).
            </p>
          ) : (
            <ScrollArea className="max-h-72 rounded-md border">
              <div className="p-2">
                {candidateKeys.map((key) => (
                  <label
                    key={key}
                    className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 hover:bg-muted"
                  >
                    <Checkbox
                      checked={addKeys.has(key)}
                      onCheckedChange={() => toggleAddKey(key)}
                    />
                    <span className="font-mono text-xs">{key}</span>
                  </label>
                ))}
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddOpen(false)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleAddMembers()}
              disabled={isBusy || addKeys.size === 0}
            >
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add {addKeys.size > 0 ? addKeys.size : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add connections dialog — multi-select over the org's connectors */}
      <Dialog
        open={isAddConnOpen}
        onOpenChange={(open) => {
          setIsAddConnOpen(open);
          if (!open) setAddConnIds(new Set());
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add connections</DialogTitle>
            <DialogDescription>
              Select the connectors this project's workflows read from and write
              to. A connector belongs to one project only.
            </DialogDescription>
          </DialogHeader>
          {candidateConnectors.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every connector is already in this project, or your organisation
              has none yet.
            </p>
          ) : (
            <ScrollArea className="max-h-72 rounded-md border">
              <div className="p-2">
                {candidateConnectors.map((c) => (
                  <label
                    key={c.Id}
                    className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 hover:bg-muted"
                  >
                    <Checkbox
                      checked={addConnIds.has(c.Id)}
                      onCheckedChange={() =>
                        setAddConnIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.Id)) next.delete(c.Id);
                          else next.add(c.Id);
                          return next;
                        })
                      }
                    />
                    <span className="flex-1 text-sm">{c.Name}</span>
                    <span className="text-xs text-muted-foreground">
                      {c.Status ?? ""}
                    </span>
                  </label>
                ))}
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddConnOpen(false)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleAddConnectors()}
              disabled={isBusy || addConnIds.size === 0}
            >
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add {addConnIds.size > 0 ? addConnIds.size : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
